import { debounce } from 'lodash';
import { useRef, useMemo, useState, useEffect } from 'react';

import {
  Box,
  Chip,
  Paper,
  Stack,
  Button,
  Select,
  Divider,
  MenuItem,
  TextField,
  IconButton,
  InputLabel,
  Typography,
  FormControl,
  Autocomplete,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { Skill } from 'src/services/jobs/jobs.types';
import { BoolNode } from 'src/services/common.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { useGetListsPaginatedQuery } from 'src/services/lists/lists.service';
import { EntityLabel, EntityLabelType } from 'src/services/utils/utils.types';
import { useGetSkillsQuery, useSearchLabelsQuery } from 'src/services/utils/utils.service';

import Iconify from 'src/components/iconify';

import { FieldOption } from '../types/search.types';
import { getFieldOptions } from '../utils/search.utils';
import {
  newRow,
  dslToRows,
  MatchType,
  rowsToDsl,
  matchesFor,
  CriteriaRow,
  valueKindOf,
} from '../utils/criteria-rows';

interface AdvancedSearchBuilderProps {
  value: BoolNode;
  onChange: (value: BoolNode) => void;
  /**
   * Readable names for values that are not words: a label ID to its label. Comes from the AI
   * response, and covers the rows it populated whose values are not in the loaded page of options.
   */
  displayNames?: Record<string, string>;
}

/**
 * A multi-select over entity IDs that displays names — used by Labels and Lists.
 *
 * IDs on BOTH sides (JT1-1635). The Labels options used to be NAMES while the AI populated the row
 * with IDs, so the moment a recruiter touched an AI-populated row it wrote a name back into a
 * filter the backend matches by ID. The row then filtered nothing, the results changed, and nothing
 * said why. Lists match by ID for the same reason, so they share the behaviour rather than a
 * near-copy of it.
 */
function IdAutocomplete({
  value,
  optionIds,
  nameFor,
  placeholder,
  onChange,
  onInputChange,
}: {
  value: string[] | undefined;
  optionIds: string[];
  nameFor: (id: string) => string;
  placeholder?: string;
  onChange: (value: string[]) => void;
  onInputChange: (value: string) => void;
}) {
  return (
    <Autocomplete
      multiple
      size="small"
      // the row's own values are options too: an AI-populated ID is usually not on the current page
      // of results, and MUI treats a value with no matching option as invalid
      options={Array.from(new Set([...(value ?? []), ...optionIds]))}
      value={value || []}
      getOptionLabel={(option) => nameFor(option)}
      onChange={(_, newValue) => onChange(newValue as string[])}
      onInputChange={(_e, typed) => onInputChange(typed)}
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            label={nameFor(option)}
            size="small"
          />
        ))
      }
      renderInput={(params) => <TextField {...params} placeholder={placeholder} />}
    />
  );
}

// Component to render a single search atom
function CriteriaRowEditor({
  atom,
  onChange,
  onRemove,
  fieldOptions,
  displayNames,
}: {
  atom: CriteriaRow;
  onChange: (row: CriteriaRow) => void;
  onRemove: () => void;
  fieldOptions: FieldOption[];
  displayNames: Record<string, string>;
}) {
  const { t } = useTranslate();
  const fieldOption = fieldOptions.find((option) => option.id === atom.field);

  const [skillsSearchQuery, setSkillsSearchQuery] = useState('');
  const [labelsSearchQuery, setLabelsSearchQuery] = useState('');
  const [listsSearchQuery, setListsSearchQuery] = useState('');

  const { data: skillsData } = useGetSkillsQuery({
    page: 1,
    per_page: 100,
    name: skillsSearchQuery
  });

  const { currentData: availableLabels } = useSearchLabelsQuery({
    label: labelsSearchQuery ?? undefined,
    type: EntityLabelType.CANDIDATE,
    page: 1,
    per_page: 10
  });

  // Candidate lists only — a contact or company list has no candidates to filter by. The endpoint
  // already scopes to what this account may read, so the dropdown cannot offer a private list.
  const { currentData: availableLists } = useGetListsPaginatedQuery({
    entity_type: ListEntityType.CANDIDATE,
    search_query: listsSearchQuery || undefined,
    page: 1,
    per_page: 10
  });

  const handleFieldChange = (field: string) => {
    const newFieldOption = fieldOptions.find((option) => option.id === field);
    onChange({
      ...atom,
      field,
      // the previous match may not exist for the new field kind, so reset to its first
      match: matchesFor(newFieldOption)[0],
      value: valueKindOf(newFieldOption) === 'list' ? [] : '',
    });
  };

  const handleMatchChange = (match: MatchType) => {
    onChange({
      ...atom,
      match,
    });
  };

  const handleValueChange = (value: any) => {
    onChange({
      ...atom,
      value,
    });
  };

  const handleVariantChange = (variant: string) => {
    onChange({
      ...atom,
      variant,
    });
  };

  const onSkillsInputChange = useMemo(
    () => debounce((value: string) => {
      setSkillsSearchQuery(value);
    }, 500),
    [setSkillsSearchQuery]
  );

  const onLabelsInputChange = useMemo(
    () => debounce((value: string) => {
      setLabelsSearchQuery(value);
    }, 500),
    [setLabelsSearchQuery]
  );

  const onListsInputChange = useMemo(
    () => debounce((value: string) => {
      setListsSearchQuery(value);
    }, 500),
    [setListsSearchQuery]
  );

  /**
   * The loaded page of labels first, then what the AI told us, then the raw value. The AI's ID is
   * usually NOT on the current page — the search returns ten at a time — so without the second
   * source an AI-populated row would show a UUID.
   */
  const labelNameFor = (id: string): string =>
    availableLabels?.results.find((label) => label.id === id)?.label
    ?? displayNames[id]
    ?? id;

  /** Same three-source resolution as labels: loaded page, then the AI's names, then the raw id. */
  const listNameFor = (id: string): string =>
    availableLists?.results.find((list) => list.id === id)?.name
    ?? displayNames[id]
    ?? id;

  const renderValueInput = () => {
    if (!fieldOption) return null;

    switch (fieldOption.type) {
      case 'text':
        return (
          <TextField
            fullWidth
            value={atom.value || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder={fieldOption.placeholder}
            size="small"
          />
        );
      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            value={atom.value || ''}
            onChange={(e) => handleValueChange(Number(e.target.value))}
            placeholder={fieldOption.placeholder}
            size="small"
            InputProps={{ inputProps: { min: 0 } }}
          />
        );
      case 'select':
        return (
          <FormControl fullWidth size="small">
            <Select
              value={atom.value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>{t('common.none')}</em>
              </MenuItem>
              {fieldOption.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'multiselect':
        if (fieldOption.id === 'skills' && skillsData) {
          return (
            <Autocomplete
              multiple
              size="small"
              freeSolo
              // commits what has been typed when the field loses focus — see the generic row below
              autoSelect
              options={skillsData.results.map((skill) => skill.name)}
              value={atom.value || []}
              onChange={(_, newValue) => handleValueChange(newValue)}
              onInputChange={(_e, value) => onSkillsInputChange(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={fieldOption.placeholder}
                />
              )}
            />
          );
        } if (fieldOption.id === 'labels' && availableLabels) {
          return (
            <IdAutocomplete
              value={atom.value}
              optionIds={availableLabels.results.map((label) => label.id)}
              nameFor={labelNameFor}
              placeholder={fieldOption.placeholder}
              onChange={handleValueChange}
              onInputChange={onLabelsInputChange}
            />
          );
        } if (fieldOption.id === 'lists' && availableLists) {
          return (
            <IdAutocomplete
              value={atom.value}
              optionIds={availableLists.results.map((list) => list.id)}
              nameFor={listNameFor}
              placeholder={fieldOption.placeholder}
              onChange={handleValueChange}
              onInputChange={onListsInputChange}
            />
          );
        }
        return (
          <Autocomplete
            multiple
            size="small"
            freeSolo
            /**
             * Commit what has been typed when the field loses focus (JT1-1654).
             *
             * These rows are tag inputs: text only becomes part of the search once Enter turns it
             * into a chip. A recruiter who typed "Client Relationship Manager" and went straight for
             * Search sent an EMPTY row, and an empty row used to drop out of the query and return
             * every candidate in the organization — reported as an ordinary "217 candidates found".
             * Clicking Search blurs this field first, so committing on blur means the search runs on
             * what they typed. The backend now refuses to widen on an empty row either; this is the
             * half that does what they meant rather than merely failing safely.
             */
            autoSelect
            options={fieldOption.options?.map((option) => option.value) || []}
            value={atom.value || []}
            onChange={(_, newValue) => handleValueChange(newValue)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={fieldOption.options?.find((o) => o.value === option)?.label || option}
                  size="small"
                />
              ))
            }
            renderOption={(props, option: string, { selected }) => {
              const optionLabel = fieldOption.options?.find((o) => o.value === option)?.label || option;
              return (
                <li {...props}>
                  <Typography variant="body2">{optionLabel}</Typography>
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={fieldOption.placeholder}
              />
            )}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Field</InputLabel>
          <Select
            value={atom.field}
            onChange={(e) => handleFieldChange(e.target.value)}
            label="Field"
          >
            {fieldOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {
          fieldOption && fieldOption.variant && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{fieldOption.variant.label}</InputLabel>
              <Select
                value={atom.variant || fieldOption.variant.options[0].value}
                onChange={(e) => handleVariantChange(e.target.value)}
                label="Period"
              >
                {fieldOption.variant.options.map((variantOption) => (
                  <MenuItem key={variantOption.value} value={variantOption.value}>
                    {variantOption.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        }

        {/* Reads as a sentence: [Skills] [is none of] [React]. Excluding no longer means building
            a NOT group — the match type carries it. */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('components.advanced_search.match')}</InputLabel>
          <Select
            value={atom.match}
            onChange={(e) => handleMatchChange(e.target.value as MatchType)}
            label={t('components.advanced_search.match')}
          >
            {matchesFor(fieldOption).map((match) => (
              <MenuItem key={match} value={match}>
                {t(`components.advanced_search.match_types.${match}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }}>{renderValueInput()}</Box>

        <IconButton onClick={onRemove} color="error">
          <Iconify icon="eva:trash-2-outline" />
        </IconButton>
      </Stack>
    </Paper>
  );
}

/**
 * A flat, AND-ed list of criteria rows (JT1-1610).
 *
 * The nested BoolNodeEditor this replaces made people build an AND/OR/NOT group to express
 * something as ordinary as "not in Melbourne": add a group, set it to NOT, then add a condition
 * inside it. A row now says Location / is none of / Melbourne, and the negation is part of the
 * sentence.
 *
 * Rows are the local source of truth and the DSL is derived from them. The reverse direction only
 * runs when a genuinely different DSL arrives from outside — the AI populating the builder — because
 * re-parsing our own output on every keystroke would rebuild every row and take the focus with it.
 */
export default function AdvancedSearchBuilder({
  value,
  onChange,
  displayNames = {},
}: AdvancedSearchBuilderProps) {
  const { t } = useTranslate();
  const fieldOptions = getFieldOptions();

  const [rows, setRows] = useState<CriteriaRow[]>(() => dslToRows(value).rows);
  const [remainder, setRemainder] = useState(() => dslToRows(value).remainder);

  // what we last emitted, so an echo of our own change does not rebuild the rows underneath the
  // user's cursor
  const lastEmitted = useRef<string>(JSON.stringify(rowsToDsl(rows, fieldOptions)));

  useEffect(() => {
    const incoming = JSON.stringify(value ?? { op: 'AND', children: [] });
    if (incoming === lastEmitted.current) return;

    const parsed = dslToRows(value);
    setRows(parsed.rows);
    setRemainder(parsed.remainder);
    lastEmitted.current = incoming;
  }, [value]);

  const emit = (next: CriteriaRow[]) => {
    setRows(next);
    const dsl = rowsToDsl(next, fieldOptions);
    lastEmitted.current = JSON.stringify(dsl);
    onChange(dsl);
  };

  const handleAddRow = () => emit([...rows, newRow(fieldOptions[0])]);

  // by id, never by index: deleting a middle row by index shifts every row after it and takes
  // their input state with it
  const handleRemoveRow = (id: string) => emit(rows.filter((row) => row.id !== id));

  const handleRowChange = (updated: CriteriaRow) =>
    emit(rows.map((row) => (row.id === updated.id ? updated : row)));

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('candidates.search.advanced_search_description')}
      </Typography>

      <Stack spacing={1.5}>
        {rows.map((row) => (
          <CriteriaRowEditor
            key={row.id}
            atom={row}
            fieldOptions={fieldOptions}
            displayNames={displayNames}
            onChange={handleRowChange}
            onRemove={() => handleRemoveRow(row.id)}
          />
        ))}

        {/* A group the flat builder cannot render. Shown rather than dropped: silently discarding
            part of somebody's search is worse than showing them something they cannot edit here. */}
        {remainder.length > 0 && (
          <Chip
            size="small"
            color="info"
            variant="soft"
            sx={{ alignSelf: 'flex-start' }}
            label={t('components.advanced_search.advanced_conditions', { count: remainder.length })}
          />
        )}

        <Button
          size="small"
          sx={{ alignSelf: 'flex-start' }}
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={handleAddRow}
        >
          {t('candidates.search.add_condition')}
        </Button>
      </Stack>
    </Box>
  );
}
